import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, LayoutGrid, List, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { STAGES, STAGE_META, fmtPrice, stagePct } from "@/lib/realestateConstants";
import { SpaceSection } from "@/components/SpaceSection";
import { PropertyCard } from "@/components/realestate/PropertyCard";
import { NewPropertyDialog } from "@/components/realestate/NewPropertyDialog";
import { DocTemplatesPanel } from "@/components/realestate/DocTemplatesPanel";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";

const PipelineColumn = ({ stage, items, workspaceId }) => {
  const meta = STAGE_META[stage];
  return (
    <div className="flex flex-col min-w-[260px] w-[260px] sm:w-auto sm:min-w-0 sm:flex-1" data-testid={`pipeline-col-${stage}`}>
      <div className="flex items-center justify-between px-1 pb-2 border-b-2" style={{ borderColor: meta.color }}>
        <span className={`text-xs font-bold uppercase tracking-wider ${meta.text}`}>{meta.label}</span>
        <span className="text-xs font-semibold text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{items.length}</span>
      </div>
      <div className="space-y-3 pt-3 flex-1">
        {items.map((p) => <PropertyCard key={p.id} property={p} workspaceId={workspaceId} />)}
        {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-xl">Sin inmuebles</p>}
      </div>
    </div>
  );
};

const PropertyRow = ({ p, workspaceId }) => {
  const meta = STAGE_META[p.stage];
  const pct = p.stage === "vendido" ? 100 : stagePct(p.progress?.[p.stage]);
  return (
    <Link to={`/space/${workspaceId}/realestate/${p.id}`} data-testid={`property-row-${p.id}`}
      className="grid grid-cols-[70px_1fr_110px_120px_140px] gap-3 items-center px-4 py-3 text-sm hover:bg-secondary/50 transition-colors">
      <span className="font-mono text-xs text-muted-foreground">{p.ref}</span>
      <div className="min-w-0"><p className="font-medium truncate">{p.title}</p><p className="text-xs text-muted-foreground truncate">{[p.address, p.city].filter(Boolean).join(", ")}</p></div>
      <span className="font-semibold">{fmtPrice(p.price)}</span>
      <span className={`text-xs font-semibold ${meta.text}`}>{meta.label}</span>
      <div className="flex items-center gap-2"><Progress value={pct} className="h-1.5" /><span className="text-xs w-9 text-right">{pct}%</span></div>
    </Link>
  );
};

export default function RealEstatePage() {
  const { id } = useParams();
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [view, setView] = useState("board");
  const [createOpen, setCreateOpen] = useState(false);

  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: ws } = useQuery({ queryKey: ["workspace", id], queryFn: async () => (await api.get(`/workspaces/${id}`)).data });
  const canWrite = ws?.my_role && ws.my_role !== "guest";
  const isAdmin = ws?.my_role === "owner" || ws?.my_role === "admin";
  const disableModule = async () => {
    if (!window.confirm("¿Quitar el módulo Inmobiliaria de este Espacio? Podrás volver a activarlo desde Plantillas.")) return;
    try {
      await api.put(`/workspaces/${id}/modules/realestate`, { enabled: false });
      qc.invalidateQueries({ queryKey: ["workspace", id] });
      toast.success("Módulo desactivado");
      navigate(`/space/${id}`);
    } catch (err) { toast.error(apiError(err)); }
  };
  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties", id, q, stage],
    queryFn: async () => (await api.get(`/workspaces/${id}/properties`, { params: { q: q || undefined, stage: stage === "all" ? undefined : stage } })).data,
  });

  const byStage = STAGES.reduce((acc, s) => ({ ...acc, [s]: properties.filter((p) => p.stage === s) }), {});

  return (
    <SpaceSection title="Inmobiliaria" actions={
      <Button size="sm" data-testid="property-create-btn" onClick={() => setCreateOpen(true)}>
        <Plus className="h-4 w-4 mr-1" /> Nuevo inmueble
      </Button>
    }>
      <div className="flex flex-wrap items-center gap-2" data-testid="realestate-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="realestate-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por referencia, dirección, vendedor…" className="pl-8 h-9" />
        </div>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-40 h-9" data-testid="realestate-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las etapas</SelectItem>
            {STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border overflow-hidden ml-auto">
          <button data-testid="realestate-view-board" onClick={() => setView("board")} className={`h-9 w-9 flex items-center justify-center ${view === "board" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} title="Pipeline"><LayoutGrid size={15} /></button>
          <button data-testid="realestate-view-list" onClick={() => setView("list")} className={`h-9 w-9 flex items-center justify-center ${view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`} title="Lista"><List size={15} /></button>
        </div>
      </div>

      {!isLoading && properties.length === 0 && !q && stage === "all" ? (
        <Card className="p-10 text-center space-y-4 border-dashed" data-testid="realestate-empty">
          <Building2 className="h-10 w-10 mx-auto text-primary" />
          <div>
            <h3 className="font-heading font-bold text-lg">Tu cartera inmobiliaria empieza aquí</h3>
            <p className="text-sm text-muted-foreground">Cada inmueble recorre Preventa → Venta → Post venta con toda su documentación, visitas y datos en una única ficha.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1" /> Añadir el primer inmueble</Button>
          {isAdmin && <div><button onClick={disableModule} className="text-xs text-muted-foreground hover:text-destructive underline" data-testid="realestate-disable-module">Quitar este módulo del Espacio</button></div>}
        </Card>
      ) : view === "board" ? (
        <div className="flex sm:grid sm:grid-cols-4 gap-4 overflow-x-auto pb-2" data-testid="realestate-pipeline">
          {STAGES.map((s) => <PipelineColumn key={s} stage={s} items={byStage[s]} workspaceId={id} />)}
        </div>
      ) : (
        <Card className="divide-y divide-border overflow-hidden" data-testid="realestate-list">
          <div className="grid grid-cols-[70px_1fr_110px_120px_140px] gap-3 px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/40">
            <span>Ref.</span><span>Inmueble</span><span>Precio</span><span>Etapa</span><span>Progreso</span>
          </div>
          {properties.map((p) => <PropertyRow key={p.id} p={p} workspaceId={id} />)}
          {properties.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Sin resultados</p>}
        </Card>
      )}

      <DocTemplatesPanel workspaceId={id} canWrite={canWrite} />

      <NewPropertyDialog workspaceId={id} open={createOpen} onOpenChange={setCreateOpen} />
    </SpaceSection>
  );
}
